import {Avatar, Input, Switch} from 'antd'
import {getId} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createSelectValue, createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Formik, FormikConsumer} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useOrgTypeLabel} from 'modules/local/useOrgTypeLabels'
import withTranslate from 'modules/local/withTranslate'
import React, {useMemo} from 'react'
import {compose, mapProps, nest, pure} from 'recompose'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import CourseClassInformation from 'views/AppLocals/Course/CourseClass/CourseClassInformation'
import {ObjectSelectField} from 'components/form/fields/ObjectSelectField'
import {ActionModalWrapper} from 'components/form/ActionModalWrapper'
import {CourseClassStudentPicker} from 'views/Workspace/Course/containers/wrappers/CourseClassStudentPicker'
import TooltipApp from 'components/TooltipApp'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import PhotoField from 'views/AppLocals/custom/PhotoField'
import {BiSolidChurch} from 'react-icons/bi'
import LookupField from 'components/LookupField'
import Translate from 'modules/local/Translate'
import {AnnouncementTypes} from 'views/AppLocals/Organization/consts/AnnouncementTypes'
import dayjs from 'dayjs'
import {createDateComponent} from 'components/form/fields/DateField'
import {
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules
} from 'components/form/ValidateForm'
import {getDateString} from 'helpers/datetimeHelpers'
import {BsChevronRight} from "react-icons/bs";

// Tách thành component đặt tên PascalCase (không phải arrow function ẩn danh gán vào key `component`)
// vì eslint rule "react-hooks/rules-of-hooks" chỉ nhận diện hook hợp lệ trong component/hook đặt tên đúng chuẩn.
const ALL_GROUPS_VALUE = '__organization__'
const GroupsInOrganizationField = ({name, value, form, onChange}) => {
    const t = useTranslate()
    const organization = _.get(form, 'values.organization', {})
    // Tên gọi "group/ministry" phụ thuộc org_type — vd Giáo xứ: "Hội đoàn", Giáo phận: "Ủy ban", Dòng tu: "Cộng đoàn"...
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('groups / ministries')
    const groupOptions = form?.values?.groups?.map(e => ({
        value: e.id,
        label: `${e?.title} (${e?.member_count} ${t('members')})`,
    })) || []
    const options = [
        {value: ALL_GROUPS_VALUE, label: t('notify entire organization')},
        ...groupOptions,
    ]
    const selectedGroup = groupOptions.find((o) => o.value === value)

    return (
        <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-color-200 tracking-wider pr-3">
                {deptLabel}
            </span>
            <SelectWidget
                name={name}
                allowClear
                className="w-full"
                value={value || ALL_GROUPS_VALUE}
                onChange={(v) => onChange(createSelectValue('group_id', v))}
                placeholder={t('notify all groups')}
                options={options}
            />
            <span className="mt-1 px-2 py-1 italic bg-slate-50 rounded-lg">
                <span className="font-semibold text-slate-500 flex-shrink-0">
                    {`* ${t('note')} : `}
                </span>
                <span className="text-slate-400">
                    {selectedGroup ? t('announcement scoped to group', {group: selectedGroup.label}) : t('announcement scope description')}
                </span>
            </span>
        </div>
    )
}

const formSchema = {
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange}) => ({
                        showCount: true,
                        maxLength: 180,
                        name,
                        value,
                        onChange,
                        placeholder: translate('title'),
                    })),
                    pure
                )(Input),
            },
        ],
    },

    content: {
        title: 'content',
        children: [
            {
                name: 'content',
                component: createControlledFormField({
                    showCount: true,
                    maxLength: 500,
                    rows: 5,
                    placeholder: 'content',
                    Component: Input.TextArea,
                }),
            },
        ],
    },

    photo: {
        title: 'image',
        children: [
            {
                name: 'photo',
                component: ({name, value, form, onChange}) => (
                    <div className="flex flex-col gap-1">
                        <PhotoField
                            holder={<BiSolidChurch/>}
                            className="background"
                            style={{padding: 0}}
                            values={_.get(form, 'values')}
                            {...{name, value, onChange}}
                        />
                        <div className="text-center text-xs text-color-500 italic">{`* 800 x 600`}</div>
                    </div>
                ),
            },
        ],
    },

    shared_url: {
        title: 'shared url',
        description: 'optional link for more information',
        children: {
            name: 'shared_url',
            component: compose(
                withTranslate,
                mapProps(({value, onChange, meta, translate}) => {
                    const error = meta?.error;
                    const touched = meta?.touched;
                    return {
                        value,
                        onChange,
                        name: 'shared_url',
                        title: touched && error ? error : '',
                        open: touched && !!error,
                        status: touched && error ? 'error' : '',
                        addonBefore: 'https://...',
                        placeholder: translate('please input url'),
                        type: 'text',
                    };
                }),
                pure
            )(nest(TooltipApp, Input)),
        },
    },

    groups_in_organization: {
        // Không dùng title/description tĩnh — tên "group/ministry" phụ thuộc org_type,
        // GroupsInOrganizationField tự resolve và tự vẽ label (xem định nghĩa phía trên).
        children: [
            {
                name: 'group_id',
                component: GroupsInOrganizationField,
            },
        ],
    },

    subject: {
        title: 'subject',
        children: [
            {
                name: 'subject',
                component: mapProps(({name, value, form, onChange}) => {
                    const {subject_id, course_class} = form?.values ?? {}
                    const {class_subjects = []} = course_class ?? {};
                    const subjects = class_subjects.map(e => ({
                        id: e.subject.id,
                        value: e.subject.id,
                        label: e.subject.title,
                        ...e.subject
                    }));

                    return {
                        name,
                        onChange,
                        onOptionChange: (option) => {
                            onChange(createValue(name, option ?? null))
                        },
                        value: _.find(subjects, {value: subject_id})?.id,
                        showSearch: true,
                        options: subjects,
                        style: {
                            padding: 0,
                            border: 0,
                        }
                    }
                })(ObjectSelectField),
            },
        ],
    },

    is_for_all: {
        title: 'for all students',
        description: 'notify all students in the class',
        children: [
            {
                name: 'is_for_all',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Switch
                                checked={value}
                                checkedChildren={<span className="font-medium italic">{t('yes')}</span>}
                                onChange={(checked) => {
                                    onChange(createValue(name, Number(checked)))
                                }}
                                className="w-fit"
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },

    students: {
        title: 'students',
        description: 'select specific students to receive this announcement',
        children: [
            {
                name: 'student_ids',
                component: mapProps(({name, value, form, onChange}) => {
                    const {course_class_id} = form?.values ?? {}
                    return {
                        name,
                        defaultValue: value,
                        mode: "multiple",
                        onChange: (value) => {
                            onChange(createValue(name, value))
                        },
                        course_class_id
                    }
                })(CourseClassStudentPicker),
            },
        ],
    },

    category: {
        title: 'announcement type',
        children: [
            {
                name: 'type',
                component: ({name, value, form}) => {
                    const values = _.get(form, 'values', {})
                    const organization = _.get(values, 'owner_type') === 'course_class'
                        ? _.get(values, 'course_class.organization', {})
                        : _.get(values, 'organization', {})
                    // owner_type='group' (group standalone, không thuộc org): không có org_type để
                    // phân nhóm loại thông báo - fallback dùng group_type của group (choir, prayer_group...)
                    // làm khóa bucket, vẫn giữ đúng cơ chế LookUp động hiện có, không tạo config tĩnh.
                    const typeBucketKey = organization?.org_type || _.get(values, 'group.group_type') || 'default'
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.type_label}
                                    valueType="value"
                                    apiPath={`/lookup/organization_announcement_type/gets?group_type=organization_announcement_${typeBucketKey}`}
                                    name={name}
                                    placeholder={t('choose or create')}
                                    handleChange={(e) => {
                                        form.setValues({
                                            ...form.values,
                                            type_label: e?.target?.value,
                                            type_code: e.target.code,
                                        });
                                    }}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => {
                                        return (
                                            <div key={i} className="flex items-center gap-3 flex-1">
                                                <Avatar
                                                    size={50}
                                                    icon={_.find(AnnouncementTypes, {value: o?.code})?.icon}
                                                    className="flex flex-center background rounded-lg"
                                                />
                                                <div className="flex flex-col flex-1">
                                                    <div
                                                        className="font-bold text-base text-color-000 tracking-wide leading-snug">
                                                        {t(o?.label)}
                                                    </div>
                                                    <div className="text-xs text-color-300 italic leading-snug">
                                                        {o?.label}
                                                    </div>
                                                </div>
                                                <BsChevronRight size={16} className="text-color-500"/>
                                            </div>
                                        )
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },

    started_finished_date: {
        inline: true,
        description: 'announcement display period',
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                name: 'time_open',
                label: 'started at',
                component: createDateComponent(),
            },
            {
                name: 'time_close',
                label: 'finished at',
                component: createDateComponent(),
            },
        ],
    },
}

const SectionLabel = ({children}) => (
    <div className="flex items-center gap-2">
        <span className="text-2xs font-bold text-slate-400 uppercase tracking-widest leading-none flex-shrink-0">
            {children}
        </span>
        <div className="flex-1 border-t border-color-50"/>
    </div>
)

const validateTitle = (title, t) => {
    const rules = [
        required(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(title, rules)
    if (message) return {title: message}
}

const validateType = (typeName, t) => {
    const rules = [
        lengthBetween(1, 120, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(typeName, rules)
    if (message) return {type: message}
}

const validateShareUrl = (shareUrl, t) => {
    if (!shareUrl) return {}
    try {
        const u = new URL(
            /^https?:\/\//i.test(shareUrl)
                ? shareUrl
                : 'https://' + shareUrl
        )
        if (!u.hostname.includes('.')) {
            return {shared_url: t('invalid url')}
        }
        return {}
    } catch {
        return {shared_url: t('invalid url')}
    }
}

const validateTimeOpen = (timeOpen, typeForm, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(timeOpen, rules)
    if (message) return {time_open: message}
    if (typeForm === 'Create') {
        const selected = dayjs(timeOpen).startOf('day')
        const today = dayjs().startOf('day')
        if (selected.isBefore(today)) {
            return {time_open: t('start date must be today or later')}
        }
    }
}

const validateTimeClose = (timeOpen, timeClose, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(timeClose, rules)
    if (message) return {time_close: message}

    const start = dayjs(timeOpen).startOf('day')
    const end = dayjs(timeClose).startOf('day')
    if (end.isBefore(start)) {
        return {time_close: t('end date must be same as or after start date')}
    }
}

const validate = (t, typeForm) => (values) => {
    const {
        title,
        shared_url,
        type_label,
        time_open,
        time_close
    } = values ?? {}
    const errorTitle = validateTitle(title, t)
    const errorType = validateType(type_label, t)
    const errorShareUrl = validateShareUrl(shared_url, t)
    const errorTimeOpen = validateTimeOpen(time_open, typeForm, t)
    const errorTimeClose = validateTimeClose(time_open, time_close, t)
    return {
        ...errorTitle,
        ...errorType,
        ...errorShareUrl,
        ...errorTimeOpen,
        ...errorTimeClose,
    }
}

const normalizeShareUrl = (shared_url) => shared_url
    ? (/^https?:\/\//i.test(shared_url) ? shared_url : 'https://' + shared_url)
    : shared_url

export const AnnouncementCreateForm = ({
                                           owner_type,
                                           organization,
                                           groups,
                                           group,
                                           course_class,
                                           initialValues,
                                           onSubmit = NullAsDefault,
                                           isSubmitting = false,
                                       }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    const isGroup = owner_type === 'group'

    const _initialValues = useMemo(
        () => ({
            title: undefined,
            content: undefined,
            photo: undefined,
            type_label: t('ANNOUNCEMENT'),
            type_code: 'ANNOUNCEMENT',
            time_open: dayjs().set("hour", 0).set("minute", 0).set("second", 0),
            time_close: dayjs().add(7, 'day').set("hour", 0).set("minute", 0).set("second", 0),
            owner_type,
            ...(isCourseClass
                ? {
                    is_for_all: 1,
                    course_class,
                    course_class_id: course_class?.id,
                    student_ids: [],
                }
                : isGroup
                    ? {group}
                    : {
                        organization,
                        groups,
                        organization_id: organization?.id,
                    }),
            ...(initialValues ?? {}),
        }),
        [owner_type, organization, groups, group, course_class, initialValues]
    )

    return (
        <Formik
            validate={validate(t, 'Create')}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                if (isCourseClass) {
                    const {
                        subject, course_class, shared_url, time_open, time_close, ...rest
                    } = values ?? {}
                    onSubmit(
                        _.omitBy(
                            {
                                shared_url: normalizeShareUrl(shared_url),
                                time_open: getDateString(time_open),
                                time_close: getDateString(time_close),
                                ...rest,
                                subject_id: !!values?.subject_id ? values?.subject_id : getId(subject),
                                student_ids: !!values?.is_for_all ? [] : values?.student_ids
                            },
                            _.isUndefined
                        )
                    )
                    return
                }

                const {organization, groups, group, photo_preview, shared_url, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            shared_url: normalizeShareUrl(shared_url),
                            ...rest,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper
                className="gap-3"
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'create', dirtyRequired: false}}
                    />
                }>
                {isCourseClass && (
                    <CourseClassInformation
                        item={course_class}
                        showDescription={false}
                    />
                )}
                <div className="flex flex-col space-y-10">
                    {!isGroup && (
                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('recipients')}</SectionLabel>
                        <Pure>
                            <FieldsFactory
                                formSchema={
                                    isCourseClass
                                        ? [formSchema.subject, formSchema.is_for_all]
                                        : [formSchema.groups_in_organization]
                                }
                            />
                        </Pure>
                        {isCourseClass && (
                            <FormikConsumer>
                                {({values}) => {
                                    const {is_for_all} = values
                                    // Pure({input: [is_for_all]}): FormikConsumer re-run mỗi khi BẤT KỲ field nào
                                    // trong form đổi giá trị (title, content...) — không khoanh vùng lại thì
                                    // formSchema={[formSchema.students]} bị tạo mảng mới liên tục, khiến
                                    // CourseClassStudentPicker bị coi như remount/gọi lại API danh sách học viên
                                    // mỗi lần gõ phím ở field khác. Chỉ nên tính lại khi is_for_all thực sự đổi.
                                    return (
                                        <Pure input={[is_for_all]}>
                                            {!!!is_for_all && (
                                                <FieldsFactory formSchema={[formSchema.students]}/>
                                            )}
                                        </Pure>
                                    )
                                }}
                            </FormikConsumer>
                        )}
                    </div>
                    )}
                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('notification content')}</SectionLabel>
                        <Pure>
                            <FieldsFactory
                                formSchema={[
                                    formSchema.title,
                                    formSchema.category,
                                    formSchema.content,
                                    formSchema.photo,
                                ]}
                            />
                        </Pure>
                    </div>



                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('display period')}</SectionLabel>
                        <Pure>
                            <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                        </Pure>
                    </div>

                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('additional info')}</SectionLabel>
                        <Pure>
                            <FieldsFactory formSchema={[formSchema.shared_url]}/>
                        </Pure>
                    </div>
                </div>
            </ActionModalWrapper>
        </Formik>
    )
}

export const AnnouncementUpdateForm = ({
                                           owner_type,
                                           organization,
                                           groups,
                                           group,
                                           course_class,
                                           initialValues,
                                           onSubmit = NullAsDefault,
                                           isSubmitting = false,
                                       }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    const isGroup = owner_type === 'group'
    const organization_id = getId(organization)
    const course_class_id = getId(course_class)

    const _initialValues = useMemo(() => {
        const {...rest} = initialValues ?? {}
        if (isCourseClass) {
            return {
                course_class_id,
                course_class,
                owner_type,
                ...rest,
                subject: initialValues?.subject?.id,
                student_ids: initialValues?.announcement_course_class_students?.map(e => e?.student?.id) || [],
                subject_id: initialValues?.subject_id || initialValues?.subject?.id,
                photo_preview: initialValues?.photo,
            }
        }
        if (isGroup) {
            return {
                group: rest?.group ?? group,
                owner_type,
                photo_preview: rest.photo,
                ...rest,
            }
        }
        return {
            organization_id,
            groups,
            group_id: rest?.group?.id,
            organization,
            owner_type,
            photo_preview: rest.photo,
            ...rest,
        }
    }, [owner_type, organization_id, course_class_id, group, initialValues])

    return (
        <Formik
            validate={validate(t, 'Edit')}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                if (isCourseClass) {
                    const {shared_url, subject, time_open, time_close, ...rest} = changes ?? {}
                    onSubmit(
                        _.omitBy(
                            {
                                shared_url: normalizeShareUrl(shared_url),
                                time_open: getDateString(time_open),
                                time_close: getDateString(time_close),
                                ...rest,
                                subject_id: !!values?.subject_id ? values?.subject_id : getId(subject),
                                student_ids: !!values?.is_for_all ? [] : values?.student_ids
                            },
                            _.isUndefined
                        )
                    )
                    return
                }

                const {shared_url, group, ...rest} = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            shared_url: normalizeShareUrl(shared_url),
                            ...rest,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper
                className="gap-4"
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'save', dirtyRequired: false}}
                    />
                }>
                {isCourseClass && (
                    <CourseClassInformation
                        item={course_class}
                        showDescription={false}
                    />
                )}
                <div className="flex flex-col space-y-10">
                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('notification content')}</SectionLabel>
                        <Pure>
                            <FieldsFactory
                                formSchema={[
                                    formSchema.title,
                                    formSchema.category,
                                    formSchema.content,
                                    formSchema.photo,
                                ]}
                            />
                        </Pure>
                    </div>

                    {!isGroup && (
                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('recipients')}</SectionLabel>
                        <Pure>
                            <FieldsFactory
                                formSchema={
                                    isCourseClass
                                        ? [formSchema.subject, formSchema.is_for_all]
                                        : [formSchema.groups_in_organization]
                                }
                            />
                        </Pure>
                        {isCourseClass && (
                            <FormikConsumer>
                                {({values}) => {
                                    const {is_for_all} = values
                                    // Pure({input: [is_for_all]}): FormikConsumer re-run mỗi khi BẤT KỲ field nào
                                    // trong form đổi giá trị (title, content...) — không khoanh vùng lại thì
                                    // formSchema={[formSchema.students]} bị tạo mảng mới liên tục, khiến
                                    // CourseClassStudentPicker bị coi như remount/gọi lại API danh sách học viên
                                    // mỗi lần gõ phím ở field khác. Chỉ nên tính lại khi is_for_all thực sự đổi.
                                    return (
                                        <Pure input={[is_for_all]}>
                                            {!!!is_for_all && (
                                                <FieldsFactory formSchema={[formSchema.students]}/>
                                            )}
                                        </Pure>
                                    )
                                }}
                            </FormikConsumer>
                        )}
                    </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('display period')}</SectionLabel>
                        <Pure>
                            <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                        </Pure>
                    </div>

                    <div className="flex flex-col gap-3">
                        <SectionLabel>{t('additional info')}</SectionLabel>
                        <Pure>
                            <FieldsFactory formSchema={[formSchema.shared_url]}/>
                        </Pure>
                    </div>
                </div>
            </ActionModalWrapper>
        </Formik>
    )
}
